/**
 * The app's own confirmations and warnings, callable from anywhere.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS IMPERATIVE AND NOT A COMPONENT
 * ---------------------------------------------------------------------------
 * This replaces `Alert.alert`, and the thing to preserve about `Alert.alert` is
 * not how it looks - it is that any code can call it. More than half the calls
 * in this app come from places that cannot conveniently hold React state: a
 * catch handler inside `AuthSheet`, a callback three components deep in
 * `HomeScreen`, a row action in `SavedAddressesScreen`. `ConfirmSheet` is the
 * declarative version and stays exactly as it is - this is for the calls that
 * would otherwise each need their own `useState`, their own pending-action
 * ref, and a callback threaded back down for a question with two buttons.
 *
 * So the shape stays imperative and the rendering moves into the app. One
 * host (`DialogHost`) mounts at the root and subscribes; these functions push
 * a request to it and hand back a promise.
 *
 * ---------------------------------------------------------------------------
 * A PROMISE, NOT AN onPress CALLBACK
 * ---------------------------------------------------------------------------
 * `Alert.alert(title, body, [{text, onPress}])` inverts control in the middle
 * of an async function, which is why so many call sites here ended up as
 * `onPress: () => void somethingAsync()` - a floating promise nobody awaits and
 * no error handler covers. `await confirm(...)` reads in the order it happens
 * and keeps the rest of the function in the same `try`.
 *
 * ---------------------------------------------------------------------------
 * NOTHING QUEUES
 * ---------------------------------------------------------------------------
 * A second request while one is open replaces it, and the first promise
 * resolves as dismissed. Queueing would show somebody a stale question about a
 * screen they have already left - the native `Alert` stacked them and that was
 * never the behaviour anybody wanted.
 *
 * ---------------------------------------------------------------------------
 * IT DRAWS `ConfirmSheet`, NOT SOMETHING NEW
 * ---------------------------------------------------------------------------
 * `DialogHost` renders the sheet this app already uses for "are you sure", so
 * a question raised from a service looks identical to one raised from a
 * screen. The rules that component enforces - the verb on the confirm button,
 * the safe way out listed second, tone on the button and not the sheet - apply
 * here unchanged.
 */

/** What the confirm button does to the world. Colours it, nothing else. */
export type DialogTone = 'danger' | 'neutral';

export interface DialogRequest {
  title: string;
  /** What happens, in a sentence. Optional for a bare title. */
  message?: string;
  /**
   * What the confirm button says - a verb, never "Yes" or "OK" alone.
   *
   * A button labelled "Yes" only reads with the question still in view, and on
   * a small screen with a keyboard up it will not be.
   */
  confirmLabel: string;
  /**
   * The way out.
   *
   * `null` means this only REPORTS - a failure, a receipt - and there is
   * nothing to decline. Two buttons that dismiss identically make somebody
   * stop and work out the difference before pressing either.
   */
  cancelLabel?: string | null;
  tone?: DialogTone;
}

type Listener = (request: DialogRequest & { id: number }) => void;

let listener: Listener | null = null;
let resolveOpen: ((confirmed: boolean) => void) | null = null;
let nextId = 1;

/**
 * Called by `DialogHost` on mount. One host only.
 *
 * Returns its own unsubscribe, so a host unmounting in a fast refresh does not
 * leave a dead reference behind that swallows every later request.
 */
export function registerDialogHost(next: Listener): () => void {
  listener = next;
  return () => {
    if (listener === next) listener = null;
  };
}

/** Called by the host when the person answers. */
export function settleDialog(confirmed: boolean): void {
  const resolve = resolveOpen;
  resolveOpen = null;
  resolve?.(confirmed);
}

/**
 * Ask something. Resolves true only on the confirm button.
 *
 * Resolves **false** when no host is mounted rather than hanging. That happens
 * in tests and during the first frames of a cold start, and a promise that
 * never settles there would silently strand whatever was awaiting it - a
 * question nobody was asked must read as "not confirmed".
 */
export function confirm(request: DialogRequest): Promise<boolean> {
  if (!listener) return Promise.resolve(false);

  // The one already open loses. See the note on queueing above.
  settleDialog(false);

  const id = nextId++;
  listener({ ...request, id });

  return new Promise<boolean>(resolve => {
    resolveOpen = resolve;
  });
}

/**
 * Say something. One button, and it only dismisses.
 *
 * The direct replacement for `Alert.alert(title, message)`, and returns
 * nothing for the same reason `Alert.alert` returned nothing: the answer to a
 * report carries no information. Handing back a promise would only invite a
 * `void` operator in front of every call to quieten the linter, which is noise
 * standing in front of the sentence that actually matters.
 */
export function notify(
  title: string,
  message?: string,
  confirmLabel = 'OK',
): void {
  confirm({
    title,
    message,
    confirmLabel,
    cancelLabel: null,
    tone: 'neutral',
  });
}
